import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];
const TITLE = "PARA//EL";

const STATS = [
  { k: "₹35K", v: "Agency Basic / 6 Weeks" },
  { k: "₹1.5L", v: "Advanced Creative + AI / 12 Weeks" },
  { k: "100%", v: "Live Industry Projects" },
  { k: "15", v: "Seats Max Per Track" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-obsidian">
      <motion.div style={{ y: yImg }} className="absolute inset-0 -z-0">
        <img
          src="https://images.unsplash.com/photo-1576280314550-773c50583407?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Camera operator silhouette on a film set"
          className="w-full h-[120%] object-cover opacity-25 grayscale contrast-125"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/40 to-obsidian" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1600px] mx-auto w-full px-5 sm:px-10 pt-32 pb-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-smoke mb-6 sm:mb-10"
          data-testid="hero-eyebrow"
        >
          Creative Education &amp; Studio Training — By Athrv Co.
        </motion.p>

        <motion.h1
          style={{ y: yTitle }}
          className="font-display font-extrabold uppercase leading-[0.82] tracking-tighter whitespace-nowrap text-[clamp(2.6rem,11.2vw,10.5rem)] text-cream select-none"
          aria-label="PARA//EL"
        >
          {TITLE.split("").map((ch, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom">
              <motion.span
                className={`inline-block ${ch === "/" ? "text-acid" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.15 + i * 0.055, ease: EASE }}
              >
                {ch}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-8 sm:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-end">
          <div className="font-head text-xl sm:text-3xl font-semibold uppercase tracking-tight leading-tight text-cream/90">
            {["Different minds.", "Different mediums.", "One direction."].map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.9 + i * 0.15, ease: EASE }}
                >
                  {i === 2 ? <span className="text-acid">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.5, ease: EASE }}
            className="flex flex-col sm:flex-row gap-4 lg:justify-end"
          >
            <button
              data-testid="hero-apply-btn"
              onClick={() => scrollToSection("apply")}
              className="bg-acid text-obsidian font-mono text-xs font-bold uppercase tracking-[0.2em] px-8 py-4 hover:bg-acid-hover hover:scale-[1.03] active:scale-[0.98] transition-transform duration-300"
            >
              Apply For Next Cohort
            </button>
            <button
              data-testid="hero-method-btn"
              onClick={() => scrollToSection("method")}
              className="border border-line text-cream font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-acid hover:text-acid transition-colors duration-300"
            >
              See The Method
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.9 }}
          className="mt-14 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 border-t border-line"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.k} className="py-5 pr-6 border-r border-line last:border-r-0 [&:nth-child(2)]:border-r-0 lg:[&:nth-child(2)]:border-r">
              <div className="font-display font-bold text-2xl sm:text-3xl text-acid">{s.k}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mt-2">{s.v}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
